import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import VendorLayout from "@/components/vendor_portal/VendorLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Plus, Share2, Archive, Trash2, CheckCircle2, Upload, FileText } from "lucide-react";
import VariantGrid from "@/components/vendor_portal/VariantGrid";
import ProductThumbnail from "@/components/catalog/ProductThumbnail";

export default function VendorCatalogDetailPage() {
  const { catalogId } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [products, setProducts] = useState([]);
  const [shares, setShares] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Scoped refresh — does NOT toggle initialLoading so child panels
  // (CatalogAttachmentsPanel etc.) keep their local state after mutations.
  // iter63c bugfix: previously `loading=true` triggered an early-return
  // that unmounted CatalogAttachmentsPanel and wiped its csvResult state,
  // so 'Imported N products.' never surfaced.
  const refresh = useCallback(async () => {
    try {
      const [c, p, s] = await Promise.all([
        api.get(`/vendor/catalogs/${catalogId}`),
        api.get(`/vendor/catalogs/${catalogId}/products`),
        api.get(`/vendor/catalogs/${catalogId}/shares`),
      ]);
      setCatalog(c.data); setProducts(p.data || []); setShares(s.data || []);
    } catch (_e) {
      navigate("/vendor/catalogs");
    } finally {
      setInitialLoading(false);
    }
  }, [catalogId, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  const activate = async () => {
    setBusy(true);
    try {
      await api.post(`/vendor/catalogs/${catalogId}/activate`);
      await refresh();
      setFeedback({ type: "success", text: "Catalog activated." });
    } catch (err) {
      setFeedback({ type: "error", text: (formatApiErrorDetail(err?.response?.data?.detail) || err?.message) || "Could not activate." });
    } finally { setBusy(false); }
  };

  const archive = async () => {
    if (!window.confirm("Archive this catalog? It will no longer be shareable.")) return;
    setBusy(true);
    try {
      await api.post(`/vendor/catalogs/${catalogId}/archive`);
      navigate("/vendor/catalogs");
    } catch (err) {
      setFeedback({ type: "error", text: (formatApiErrorDetail(err?.response?.data?.detail) || err?.message) || "Could not archive." });
      setBusy(false);
    }
  };

  const removeProduct = async (product_id) => {
    if (!window.confirm("Remove this product from the catalog?")) return;
    try {
      await api.delete(`/vendor/catalogs/${catalogId}/products/${product_id}`);
      refresh();
    } catch (err) {
      alert((formatApiErrorDetail(err?.response?.data?.detail) || err?.message) || "Delete failed.");
    }
  };

  if (initialLoading) return <VendorLayout><div>Loading…</div></VendorLayout>;
  if (!catalog) return null;

  return (
    <VendorLayout>
      <section data-testid="vendor-catalog-detail">
        <button
          type="button"
          onClick={() => navigate("/vendor/catalogs")}
          data-testid="vendor-catalog-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#7A7A7A] hover:text-[#1A422D]"
        >
          <ArrowLeft size={14} /> Back to catalogs
        </button>

        <div className="mt-4 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Catalog · {catalog.status}</div>
            <h1 className="font-serif text-4xl text-[#1A422D] mt-2">{catalog.name}</h1>
            <div className="mt-1 text-xs text-[#7A7A7A]">
              {catalog.season || "—"} · {catalog.year || "—"} · {catalog.catalog_type || "General"}
            </div>
            {catalog.description && (
              <p className="mt-3 text-sm text-[#4A4A4A] max-w-2xl">{catalog.description}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {catalog.status === "draft" && (
              <button onClick={activate} disabled={busy} data-testid="vendor-activate-catalog" className="btn-primary text-xs">
                <CheckCircle2 size={13} className="inline mr-1" /> Activate
              </button>
            )}
            {catalog.status !== "draft" && catalog.status !== "archived" && (
              <button onClick={() => setShowShare(true)} data-testid="vendor-share-catalog" className="btn-primary text-xs">
                <Share2 size={13} className="inline mr-1" /> Share Catalog
              </button>
            )}
            {catalog.status !== "archived" && (
              <button onClick={archive} disabled={busy} data-testid="vendor-archive-catalog" className="btn-secondary text-xs">
                <Archive size={13} className="inline mr-1" /> Archive
              </button>
            )}
          </div>
        </div>

        {feedback && (
          <div className={`mt-4 text-xs ${feedback.type === "error" ? "text-[#a33]" : "text-[#1A422D]"}`} data-testid="vendor-catalog-feedback">
            {feedback.text}
          </div>
        )}

        {/* CSV import + PDF upload */}
        {catalog.status !== "archived" && (
          <CatalogAttachmentsPanel
            catalog={catalog}
            onChanged={refresh}
          />
        )}

        {/* Products list */}
        <div className="mt-10 flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[#1A422D]">Products <span className="text-sm text-[#7A7A7A]">({products.length})</span></h2>
          <button onClick={() => setShowAddProduct(true)} data-testid="vendor-add-product" className="btn-secondary inline-flex items-center gap-2 text-xs">
            <Plus size={14} /> Add Product
          </button>
        </div>

        {products.length === 0 ? (
          <div className="mt-4 p-8 border border-dashed border-[#B59A5A]/40 text-center text-sm text-[#7A7A7A]" data-testid="vendor-products-empty">
            No products yet. Add your first product to activate + share this catalog.
          </div>
        ) : (
          <div className="mt-4 border border-[#E5E3D8] bg-white divide-y divide-[#F0EDE0]" data-testid="vendor-products-list">
            {products.map((p) => (
              <div key={p.product_id} className="p-4 flex items-center gap-4" data-testid={`vendor-product-row-${p.product_id}`}>
                <ProductThumbnail product={p} size="row" testId={`vendor-product-thumb-${p.product_id}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-[#1A422D] font-medium truncate">{p.product_name}</div>
                  <div className="text-xs text-[#7A7A7A] mt-1">
                    {p.style_number ? `Style #${p.style_number} · ` : ""}
                    {p.category || "—"} · {p.availability_status || "available"}
                  </div>
                  <div className="text-xs text-[#4A4A4A] mt-1">
                    {p.wholesale_price != null ? `Wholesale $${p.wholesale_price}` : ""}
                    {p.msrp != null ? ` · MSRP $${p.msrp}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeProduct(p.product_id)}
                  data-testid={`vendor-product-delete-${p.product_id}`}
                  className="text-[10px] uppercase tracking-widest text-[#a33] hover:underline shrink-0"
                >
                  <Trash2 size={13} className="inline" /> Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Share status */}
        {shares.length > 0 && (
          <div className="mt-10">
            <h2 className="font-serif text-2xl text-[#1A422D]">Sharing</h2>
            <div className="mt-3 border border-[#E5E3D8] bg-white divide-y divide-[#F0EDE0]" data-testid="vendor-shares-list">
              {shares.map((s) => (
                <div key={s.share_id} className="p-4 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-[#1A422D] font-medium">{s.club?.name || s.club_id}</div>
                    <div className="text-xs text-[#7A7A7A] mt-1">
                      Shared {s.shared_at?.slice(0, 10)}{s.viewed_at ? ` · Viewed ${s.viewed_at.slice(0, 10)}` : ""}
                    </div>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#B59A5A]">{s.status}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {showAddProduct && (
        <AddProductModal
          catalogId={catalogId}
          onClose={() => setShowAddProduct(false)}
          onAdded={() => { setShowAddProduct(false); refresh(); }}
        />
      )}
      {showShare && (
        <ShareCatalogModal
          catalogId={catalogId}
          existingShares={shares}
          onClose={() => setShowShare(false)}
          onShared={() => { setShowShare(false); refresh(); }}
        />
      )}
    </VendorLayout>
  );
}

function AddProductModal({ catalogId, onClose, onAdded }) {
  const [form, setForm] = useState({
    product_name: "", style_number: "", category: "", description: "",
    colors: "", sizes: "", wholesale_price: "", msrp: "",
  });
  const [variants, setVariants] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const colorsArr = form.colors.split(",").map((s) => s.trim()).filter(Boolean);
  const sizesArr = form.sizes.split(",").map((s) => s.trim()).filter(Boolean);

  const submit = async () => {
    if (!form.product_name.trim()) { setError("Product name is required."); return; }
    setBusy(true); setError(null);
    try {
      const body = {
        product_name: form.product_name.trim(),
        style_number: form.style_number.trim() || undefined,
        category: form.category.trim() || undefined,
        description: form.description.trim() || undefined,
        colors: colorsArr,
        sizes: sizesArr,
        wholesale_price: form.wholesale_price ? parseFloat(form.wholesale_price) : undefined,
        msrp: form.msrp ? parseFloat(form.msrp) : undefined,
        variants: variants.length > 0 ? variants : undefined,
      };
      Object.keys(body).forEach((k) => {
        if (body[k] === undefined || body[k] === "" || (Array.isArray(body[k]) && body[k].length === 0)) delete body[k];
      });
      await api.post(`/vendor/catalogs/${catalogId}/products`, body);
      onAdded();
    } catch (err) {
      setError((formatApiErrorDetail(err?.response?.data?.detail) || err?.message) || "Could not add product.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="add-product-modal">
      <div className="bg-white w-full max-w-2xl p-8 border border-[#E5E3D8] max-h-[90vh] overflow-y-auto">
        <div className="overline">Add Product</div>
        <h2 className="font-serif text-2xl text-[#1A422D] mt-1">New Product</h2>

        <div className="mt-6 space-y-3">
          <div>
            <label className="label-elegant block mb-1">Product Name</label>
            <input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} data-testid="new-product-name" className="input-field" placeholder="Excursionist Quarter-Zip" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-elegant block mb-1">Style #</label>
              <input value={form.style_number} onChange={(e) => setForm({ ...form, style_number: e.target.value })} data-testid="new-product-style" className="input-field" />
            </div>
            <div>
              <label className="label-elegant block mb-1">Category</label>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="new-product-category" className="input-field" placeholder="Quarter Zips" />
            </div>
          </div>
          <div>
            <label className="label-elegant block mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-product-desc" className="input-field" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-elegant block mb-1">Colors (comma-separated)</label>
              <input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} data-testid="new-product-colors" className="input-field" placeholder="Navy, White, Grey" />
            </div>
            <div>
              <label className="label-elegant block mb-1">Sizes (comma-separated)</label>
              <input value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} data-testid="new-product-sizes" className="input-field" placeholder="S, M, L, XL" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-elegant block mb-1">Wholesale Price</label>
              <input type="number" step="0.01" value={form.wholesale_price} onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })} data-testid="new-product-wholesale" className="input-field" />
            </div>
            <div>
              <label className="label-elegant block mb-1">MSRP</label>
              <input type="number" step="0.01" value={form.msrp} onChange={(e) => setForm({ ...form, msrp: e.target.value })} data-testid="new-product-msrp" className="input-field" />
            </div>
          </div>

          {/* Variant grid (iter63 Phase 1C) */}
          <div>
            <label className="label-elegant block mb-2">Variants (Color × Size)</label>
            <VariantGrid colors={colorsArr} sizes={sizesArr} variants={variants} onChange={setVariants} />
          </div>
        </div>

        {error && <div className="mt-4 text-xs text-[#a33]" data-testid="add-product-error">{error}</div>}

        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} data-testid="add-product-cancel" className="btn-secondary text-xs">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="add-product-submit" className="btn-primary">
            {busy ? "Adding…" : "Add Product"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareCatalogModal({ catalogId, existingShares, onClose, onShared }) {
  const [clubId, setClubId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!clubId.trim()) { setError("Club ID is required."); return; }
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(`/vendor/catalogs/${catalogId}/share`, {
        club_ids: [clubId.trim()], note: note.trim() || undefined,
      });
      setResult(data);
      if (data.shared?.length) setTimeout(onShared, 1200);
    } catch (err) {
      setError((formatApiErrorDetail(err?.response?.data?.detail) || err?.message) || "Share failed.");
    } finally { setBusy(false); }
  };

  const alreadyIds = new Set(existingShares.map((s) => s.club_id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="share-catalog-modal">
      <div className="bg-white w-full max-w-lg p-8 border border-[#E5E3D8]">
        <div className="overline">Share Catalog</div>
        <h2 className="font-serif text-2xl text-[#1A422D] mt-1">Send to a Golf Shop</h2>
        <p className="text-xs text-[#7A7A7A] mt-2">
          Golf shops must approve you before your catalog reaches them. If share fails silently, contact the club directly.
        </p>

        <div className="mt-6 space-y-3">
          <div>
            <label className="label-elegant block mb-1">Club ID</label>
            <input value={clubId} onChange={(e) => setClubId(e.target.value)} data-testid="share-club-id" className="input-field" placeholder="club_xxx" />
          </div>
          <div>
            <label className="label-elegant block mb-1">Note (optional)</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="share-note" className="input-field" />
          </div>
        </div>

        {result && (
          <div className="mt-4 text-xs space-y-1" data-testid="share-result">
            {result.shared?.length > 0 && (
              <div className="text-[#1A422D]">Shared with {result.shared.length} club(s).</div>
            )}
            {result.not_approved_club_ids?.length > 0 && (
              <div className="text-[#a33]">Not approved by: {result.not_approved_club_ids.join(", ")}</div>
            )}
            {result.already_shared_club_ids?.length > 0 && (
              <div className="text-[#7A7A7A]">Already shared with: {result.already_shared_club_ids.join(", ")}</div>
            )}
          </div>
        )}
        {error && <div className="mt-4 text-xs text-[#a33]">{error}</div>}
        {alreadyIds.size > 0 && (
          <div className="mt-3 text-[10px] text-[#7A7A7A]">
            Currently shared with: {[...alreadyIds].join(", ")}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} data-testid="share-cancel" className="btn-secondary text-xs">Close</button>
          <button onClick={submit} disabled={busy} data-testid="share-submit" className="btn-primary">
            {busy ? "Sharing…" : "Share Catalog"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------ iter63 Phase 1C · Attachments Panel ------------------
// Bulk CSV product import + PDF catalog upload/preview/delete.
function CatalogAttachmentsPanel({ catalog, onChanged }) {
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow the same file to be re-selected later
    if (!file) return;
    setCsvBusy(true); setCsvResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(
        `/vendor/catalogs/${catalog.catalog_id}/products/csv-import`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setCsvResult(data);
      onChanged?.();
    } catch (err) {
      setCsvResult({ error: (formatApiErrorDetail(err?.response?.data?.detail) || err?.message) || "CSV import failed." });
    } finally {
      setCsvBusy(false);
    }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPdfBusy(true); setPdfError(null);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(
        `/vendor/catalogs/${catalog.catalog_id}/pdf`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onChanged?.();
    } catch (err) {
      setPdfError((formatApiErrorDetail(err?.response?.data?.detail) || err?.message) || "PDF upload failed.");
    } finally {
      setPdfBusy(false);
    }
  };

  const deletePdf = async () => {
    if (!window.confirm("Remove the attached PDF from this catalog?")) return;
    try { await api.delete(`/vendor/catalogs/${catalog.catalog_id}/pdf`); onChanged?.(); }
    catch (err) { setPdfError((formatApiErrorDetail(err?.response?.data?.detail) || err?.message) || "Delete failed."); }
  };

  const pdfUrl = catalog.pdf_file_id
    ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/vendor/catalogs/${catalog.catalog_id}/pdf`
    : null;

  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vendor-attachments-panel">
      {/* CSV import */}
      <div className="p-5 border border-[#E5E3D8] bg-white">
        <div className="flex items-center gap-2">
          <Upload size={16} className="text-[#B59A5A]" />
          <h3 className="font-serif text-lg text-[#1A422D]">Import Products from CSV</h3>
        </div>
        <p className="text-xs text-[#7A7A7A] mt-2 leading-relaxed">
          Column headers accepted (case-insensitive): <code>product_name</code> (required), <code>style_number</code>, <code>sku</code>, <code>category</code>, <code>colors</code>, <code>sizes</code>, <code>wholesale_price</code>, <code>msrp</code>, <code>availability_status</code>, plus decoration flags. Multi-value cells split on <code>|</code>, <code>;</code>, or <code>,</code>.
        </p>
        <label className="mt-4 inline-block cursor-pointer btn-secondary text-xs" data-testid="csv-import-label">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={uploadCsv}
            disabled={csvBusy}
            data-testid="csv-import-input"
            className="hidden"
          />
          <Upload size={13} className="inline mr-1" /> {csvBusy ? "Importing…" : "Choose CSV"}
        </label>

        {csvResult && (
          <div className="mt-4 text-xs" data-testid="csv-import-result">
            {csvResult.error ? (
              <div className="text-[#a33]">{csvResult.error}</div>
            ) : (
              <>
                <div className="text-[#1A422D]" data-testid="csv-import-created">
                  Imported {csvResult.created_count} product{csvResult.created_count === 1 ? "" : "s"}.
                </div>
                {csvResult.errors?.length > 0 && (
                  <div className="mt-2 text-[#a33]" data-testid="csv-import-errors">
                    <div>{csvResult.errors.length} row(s) failed:</div>
                    <ul className="mt-1 list-disc list-inside">
                      {csvResult.errors.slice(0, 5).map((e, i) => (
                        <li key={i}>Row {e.row}: {e.message}</li>
                      ))}
                      {csvResult.errors.length > 5 && <li>… and {csvResult.errors.length - 5} more</li>}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* PDF upload */}
      <div className="p-5 border border-[#E5E3D8] bg-white">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-[#B59A5A]" />
          <h3 className="font-serif text-lg text-[#1A422D]">Catalog PDF</h3>
        </div>
        <p className="text-xs text-[#7A7A7A] mt-2 leading-relaxed">
          Attach a print catalog PDF (≤25MB). Approved golf shops can view it alongside the itemized product list. Members never see the PDF.
        </p>

        {catalog.pdf_file_id ? (
          <div className="mt-4 space-y-2" data-testid="pdf-current">
            <div className="p-3 border border-[#F0EDE0] bg-[#F9F8F4] text-xs text-[#1A422D]">
              PDF attached · <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="underline" data-testid="pdf-open-link">Open in new tab</a>
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="cursor-pointer btn-secondary text-xs">
                <input type="file" accept="application/pdf" onChange={uploadPdf} disabled={pdfBusy} data-testid="pdf-replace-input" className="hidden" />
                {pdfBusy ? "Uploading…" : "Replace PDF"}
              </label>
              <button onClick={deletePdf} data-testid="pdf-delete" className="text-xs text-[#a33] hover:underline">Remove PDF</button>
            </div>
          </div>
        ) : (
          <label className="mt-4 inline-block cursor-pointer btn-secondary text-xs" data-testid="pdf-upload-label">
            <input type="file" accept="application/pdf" onChange={uploadPdf} disabled={pdfBusy} data-testid="pdf-upload-input" className="hidden" />
            <Upload size={13} className="inline mr-1" /> {pdfBusy ? "Uploading…" : "Choose PDF"}
          </label>
        )}

        {pdfError && <div className="mt-3 text-xs text-[#a33]" data-testid="pdf-error">{pdfError}</div>}
      </div>
    </div>
  );
}

